import React, {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import graphql from "babel-plugin-relay/macro";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";
import { ErrorBoundary } from "react-error-boundary";

import type {
  Viewer_getViewer_Query,
  Viewer_getViewer_Query$data,
} from "api/__generated__/Viewer_getViewer_Query.graphql";
import type Permission from "api/Permission";
import { useAPIErrors } from "contexts/Relay";
import { useSession } from "contexts/Session";
import Spinner from "components/Spinner";

const GET_VIEWER_QUERY = graphql`
  query Viewer_getViewer_Query {
    viewer {
      id
      permissions
      organization {
        thin
      }
    }
  }
`;

type Viewer = Viewer_getViewer_Query$data["viewer"];

const QueryError = ({ errorCallback }: { errorCallback: () => void }) => {
  errorCallback();
  return <React.Fragment />;
};

type ViewerQueryProps = {
  getViewerQuery?: PreloadedQuery<Viewer_getViewer_Query> | null;
  onQueryFinished: (viewer: Viewer) => void;
};

const ViewerQuery = ({ getViewerQuery, onQueryFinished }: ViewerQueryProps) => {
  if (!getViewerQuery) {
    return null;
  }

  return (
    <ErrorBoundary
      fallback={<QueryError errorCallback={() => onQueryFinished(null)} />}
    >
      <Suspense
        fallback={
          <div data-testid="viewer-context-loading" className="d-none" />
        }
      >
        <ViewerData
          getViewerQuery={getViewerQuery}
          onQueryFinished={onQueryFinished}
        />
      </Suspense>
    </ErrorBoundary>
  );
};

type ViewerDataProps = {
  getViewerQuery: PreloadedQuery<Viewer_getViewer_Query>;
  onQueryFinished: (viewer: Viewer) => void;
};

const ViewerData = ({ getViewerQuery, onQueryFinished }: ViewerDataProps) => {
  const viewerPermissionsData = usePreloadedQuery<Viewer_getViewer_Query>(
    GET_VIEWER_QUERY,
    getViewerQuery
  );

  useEffect(() => {
    onQueryFinished(viewerPermissionsData?.viewer);
  }, [onQueryFinished, viewerPermissionsData]);

  return null;
};

type ViewerContextValue = {
  readonly acceptedLatestPrivacyPolicy: boolean;
  readonly acceptedLatestTermsAndConditions: boolean;
  readonly isAuthenticated: boolean;
  readonly isThinOrganization: boolean;
  readonly userId: string | null;
  readonly userPermissions: Readonly<Permission[]>;
  readonly setAcceptedLatestPrivacyPolicy: (accepted: boolean) => void;
  readonly setAcceptedLatestTermsAndConditions: (accepted: boolean) => void;
  readonly refresh: () => void;
};

const ViewerContext = createContext<ViewerContextValue | null>(null);

interface ViewerProviderProps {
  children: React.ReactNode;
}

const ViewerProvider = ({ children }: ViewerProviderProps) => {
  const apiErrors = useAPIErrors();
  const { authToken, setAuthToken } = useSession();
  const [initalTokenValidation, setInitalTokenValidation] = useState(
    !!authToken
  );
  const [getViewerQuery, getViewer] = useQueryLoader<Viewer_getViewer_Query>(
    GET_VIEWER_QUERY
  );
  // we assume users have accepted the latest PP. Set to false at the first failed query
  // TODO implement check when retrieving the viewer
  const [
    acceptedLatestPrivacyPolicy,
    setAcceptedLatestPrivacyPolicy,
  ] = useState(true);
  // we assume users have accepted the latest T&C. Set to false at the first failed query
  // TODO implement check when retrieving the viewer
  const [
    acceptedLatestTermsAndConditions,
    setAcceptedLatestTermsAndConditions,
  ] = useState(true);
  const [viewer, setViewer] = useState<Viewer>(null);

  const handleViewerQuery = useCallback(
    (result: Viewer) => {
      if (result) {
        setViewer(result);
      } else {
        setAuthToken(null);
      }
      setInitalTokenValidation(false);
    },
    [setAuthToken]
  );

  useEffect(() => {
    setViewer(null);
    if (authToken) {
      // TODO for a smoother UX, the login mutation should return the viewer node
      getViewer({}, { fetchPolicy: "network-only" });
    }
  }, [authToken, getViewer]);

  useEffect(() => {
    if (apiErrors.has("privacyPolicyError")) {
      setAcceptedLatestPrivacyPolicy(false);
    }
    if (apiErrors.has("termsAndConditionsError")) {
      setAcceptedLatestTermsAndConditions(false);
    }
  }, [apiErrors]);

  const contextValue: ViewerContextValue = useMemo(
    () => ({
      acceptedLatestPrivacyPolicy,
      acceptedLatestTermsAndConditions,
      isAuthenticated: !!viewer,
      isThinOrganization: viewer ? viewer.organization.thin : true,
      userId: viewer ? viewer.id : null,
      userPermissions: viewer ? viewer.permissions : [],
      setAcceptedLatestPrivacyPolicy,
      setAcceptedLatestTermsAndConditions,
      refresh: () => getViewer({}, { fetchPolicy: "network-only" }),
    }),
    [
      viewer,
      acceptedLatestPrivacyPolicy,
      acceptedLatestTermsAndConditions,
      getViewer,
    ]
  );

  return (
    <ViewerContext.Provider value={contextValue}>
      <ViewerQuery
        getViewerQuery={getViewerQuery}
        onQueryFinished={handleViewerQuery}
      />
      {initalTokenValidation ? (
        <div className="vh-100 d-flex justify-content-center align-items-center">
          <Spinner />
        </div>
      ) : (
        children
      )}
    </ViewerContext.Provider>
  );
};

const useViewer = (): ViewerContextValue => {
  const contextValue = useContext(ViewerContext);
  if (contextValue == null) {
    throw new Error("AuthContext has not been Provided");
  }
  return contextValue;
};

export { useViewer, ViewerContext };
export type { ViewerContextValue };

export default ViewerProvider;
